import React, { useEffect, useState } from "react";
import { MdLocalShipping, MdLock } from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { fetchRuns } from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import RunDialog from "./RunDialog";
import SheetChip from "./SheetChip";
import {
  driverName,
  isLockedRun,
  isOpenRun,
  lockedByText,
  lockedText,
  RUN_STATUS,
  runDay,
  runTitle,
} from "./runs";

const TABS = [
  ["open", "Open", isOpenRun],
  ["completed", "Completed", (run) => run.status === "completed"],
  ["cancelled", "Cancelled", (run) => run.status === "cancelled"],
  ["all", "All", () => true],
];

// "3/12 done", counting every stop with an outcome, those fulfilled outside
// the run too.
export const runProgress = (run) => {
  const done =
    Number(run?.delivered_count ?? 0) +
    Number(run?.partly_count ?? 0) +
    Number(run?.not_delivered_count ?? 0) +
    Number(run?.fulfilled_count ?? 0);
  return `${done}/${Number(run?.stops_count ?? 0)} done`;
};

/**
 * Runs: the account's runs (newest date first), Open by default; a locked run
 * says so, and who locked it. Clicking a run calls `onOpenRun(id)`, which
 * opens it on the route sheet.
 */
const RunsModal = ({ open, onOpenRun, onClose }) => {
  const [tab, setTab] = useState("open");
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchRuns().then((response) => {
      if (cancelled) return;
      const list = response?.data?.result?.data;
      setFailed(Boolean(response.error) || !Array.isArray(list));
      setRuns(Array.isArray(list) ? list : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, attempt]);

  useEffect(() => {
    if (open) setTab("open");
  }, [open]);

  const [, , matches] = TABS.find(([key]) => key === tab);
  const shown = runs.filter(matches);

  return (
    <RunDialog
      open={open}
      labelId="runsTitle"
      title="Runs"
      subtitle="Dated trips with a driver and the stops in order"
      icon={MdLocalShipping}
      onClose={onClose}
      footer={
        <BoltButton
          type="button"
          variant="outline"
          size="sm"
          label="Close"
          onClick={onClose}
        />
      }
    >
      <div
        role="tablist"
        aria-label="Runs"
        className="flex gap-[4px] overflow-x-auto"
      >
        {TABS.map(([key, label, test]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`shrink-0 rounded-full border-[1px] px-[11px] py-[4px] text-[12px] font-semibold ${
              tab === key
                ? "border-brand bg-brand-light text-brand"
                : "border-ui-border bg-ui-surface text-ui-text-muted hover:text-ui-text"
            }`}
          >
            {label}
            {!loading && (
              <span className="ml-[5px] opacity-70">
                {runs.filter(test).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid min-h-[140px] place-items-center">
          <CircularLoader size={24} />
        </div>
      ) : failed ? (
        <div className="flex flex-wrap items-center justify-between gap-[8px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[10px] text-[12px] text-state-danger-text">
          <span>Couldn't load the runs.</span>
          <BoltButton
            type="button"
            variant="outline"
            size="sm"
            label="Try again"
            onClick={() => setAttempt((count) => count + 1)}
          />
        </div>
      ) : shown.length === 0 ? (
        <p className="m-[0px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface px-[12px] py-[14px] text-[12px] text-ui-text-muted">
          {tab === "open"
            ? "No open runs. Tick orders and use Create run."
            : "No runs here."}
        </p>
      ) : (
        <ul className="m-[0px] list-none overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface p-[0px]">
          {shown.map((run) => {
            const [label, tone] = RUN_STATUS[run.status] ?? [
              run.status,
              "neutral",
            ];
            const locked = isLockedRun(run);
            return (
              <li
                key={run.id}
                className="border-b-[1px] border-ui-border-subtle last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() => onOpenRun(run.id)}
                  className="flex min-h-[44px] w-full items-center gap-[10px] px-[12px] py-[8px] text-left hover:bg-ui-surface-sunken"
                >
                  <span
                    aria-hidden="true"
                    className="h-[10px] w-[10px] shrink-0 rounded-full border-[1px] border-ui-border"
                    style={{ background: run.route?.color || "transparent" }}
                  />
                  <div className="grid min-w-[0px] flex-1 gap-[1px]">
                    <span className="truncate font-semibold">
                      {runTitle(run)}
                    </span>
                    <span className="truncate text-ui-text-muted">
                      {[
                        runDay(run.run_date),
                        driverName(run.driver) || "No driver",
                        runProgress(run),
                        locked && lockedByText(run),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                  {locked && (
                    <span title={lockedText(run)} className="flex shrink-0">
                      <SheetChip tone="warning">
                        <MdLock
                          aria-hidden="true"
                          className="h-[11px] w-[11px]"
                        />
                        Locked
                      </SheetChip>
                    </span>
                  )}
                  <SheetChip tone={tone}>{label}</SheetChip>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </RunDialog>
  );
};

export default RunsModal;
